export type CompareFunction = (one: unknown, next: unknown) => number
export type SortingDirection = SortingDirectionMap[keyof SortingDirectionMap]

type SortingDirecitonWithoutNone = SortingDirectionMap[keyof Omit<
  SortingDirectionMap,
  'NONE'
>]
type SortingDirectionMap = {
  NONE: 'SORTING_DIRECTION::NONE'
  ASC: 'SORTING_DIRECTION::ASC'
  DESC: 'SORTING_DIRECTION::DESC'
}
export const SORTING_DIRECTION: SortingDirectionMap = {
  NONE: 'SORTING_DIRECTION::NONE',
  ASC: 'SORTING_DIRECTION::ASC',
  DESC: 'SORTING_DIRECTION::DESC',
}

const _SORTING_DIRECTION_TOSTRING_MAP: Record<
  SortingDirection,
  keyof SortingDirectionMap
> = {
  'SORTING_DIRECTION::NONE': 'NONE',
  'SORTING_DIRECTION::ASC': 'ASC',
  'SORTING_DIRECTION::DESC': 'DESC',
}
export const directionToString = (
  direction: SortingDirection
): keyof SortingDirectionMap => _SORTING_DIRECTION_TOSTRING_MAP[direction]

export const SORTING_ORDER: ReadonlyArray<
  SortingDirectionMap[keyof SortingDirectionMap]
> = [SORTING_DIRECTION.NONE, SORTING_DIRECTION.ASC, SORTING_DIRECTION.DESC]

export const safeCastToInteger = (value: unknown): number => {
  if (typeof value === 'number') {
    return Math.trunc(value)
  }
  if (typeof value !== 'string') {
    return 0
  }
  const parsed = parseInt(value, 10)
  return isNaN(parsed) ? 0 : parsed
}
const safeCastToFloat = (value: unknown): number => {
  if (typeof value === 'number') {
    return value
  }
  if (typeof value !== 'string') {
    return 0
  }
  return parseFloat(value ?? '0')
}

export const COMPARE_FUNCTION: Record<string, CompareFunction> = {
  INTEGER: (one, next) => safeCastToInteger(one) - safeCastToInteger(next),
  FLOAT: (one, next) => safeCastToFloat(one) - safeCastToFloat(next),
  DATE: (one: unknown, next: unknown) => {
    if (!(one instanceof Date) || !(next instanceof Date)) {
      return 0
    }
    return one.getTime() - next.getTime()
  },
  STRING: (one, next) => {
    if (typeof one !== 'string' || typeof next !== 'string') {
      return 0
    }
    const oneString = one ? one.toString().toUpperCase() : ''
    const nextString = next ? next.toString().toUpperCase() : ''
    if (oneString > nextString) {
      return 1
    }
    if (oneString < nextString) {
      return -1
    }
    return 0
  },
}

export const computeNextSortingDirection = (
  current: SortingDirection
): SortingDirection => {
  const nextIndex = SORTING_ORDER.indexOf(current) + 1
  return nextIndex >= SORTING_ORDER.length
    ? SORTING_ORDER[0]
    : SORTING_ORDER[nextIndex]
}

export type WithIndex<T> = { data: T; index: number }
export const wrapWithIndex = <T>(list: ReadonlyArray<T>) =>
  list.map((data, index): WithIndex<T> => ({ data, index }))

export type SortResult = ReadonlyArray<number> // index
export const sort = <T>(
  data: ReadonlyArray<WithIndex<T>>,
  key: keyof T,
  direction: SortingDirecitonWithoutNone,
  compareFunction?: CompareFunction
): SortResult => {
  const compare =
    typeof compareFunction === 'function'
      ? compareFunction
      : COMPARE_FUNCTION.STRING
  const sortingFactor = direction === SORTING_DIRECTION.ASC ? 1 : -1

  // eslint-disable-next-line fp/no-mutating-methods
  return [...data]
    .sort(
      (one: WithIndex<T>, next: WithIndex<T>): number =>
        compare(one.data[key], next.data[key]) * sortingFactor ||
        one.index - next.index
    )
    .map(({ index }) => index)
}
